import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Grid,
  TextField,
  Button,
  Alert,
  DialogContent,
  DialogActions,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import axios, { AxiosError } from "axios";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useQuery, useMutation } from "react-query";
import * as yup from "yup";
import { queryClient } from "../App";
import { API_URL } from "../consts";
import { RisorseSpecifiche, RisorseTipologie, Tipologia_Uso } from "../prismaTypes";
import { Loading } from "../ui/Loading";
import { getString } from "../utils/getString";

type Inputs = {
  id?: number;
  descrizioneUso: string;
  Risorse_TipologieId: number;
  minPartecipanti: number | null;
  maxPartecipanti: number | null;
  note: string;
  deleted: boolean;
  sigla: string;
};
const defaultValues = {
  descrizioneUso: "",
  Risorse_TipologieId: 0,
  minPartecipanti: null,
  maxPartecipanti: null,
  note: "",
  deleted: false,
  sigla: "",
};
const schema = yup
  .object({
    deleted: yup.boolean(),
    descrizioneUso: yup.string().required(),
    note: yup.string().optional(),
    sigla: yup.string().optional(),
  })
  .required();

export function TipologiaUso(props: { id?: number; closeDialog: () => void }) {
  let { id, closeDialog } = props;

  const [loaded, setLoaded] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const { refetch } = useQuery<{ tipologia: Tipologia_Uso }, AxiosError>(
    ["tipologie-uso", id],
    () => axios.get(API_URL + "tipologie-uso/get/" + id).then((res) => res.data),
    {
      enabled: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        queryClient.invalidateQueries();
        const { tipologia } = data;
        setLoaded(true);

        setValue("descrizioneUso", getString(tipologia.descrizioneUso));
        setValue("Risorse_TipologieId", tipologia.Risorse_TipologieId);
        setValue("minPartecipanti", tipologia.minPartecipanti);
        setValue("maxPartecipanti", tipologia.maxPartecipanti);
        setValue("note", getString(tipologia.note));
        setValue("deleted", tipologia.deleted);
        setValue("sigla", getString(tipologia.sigla));
      },
      onError: (error: AxiosError) => {
        console.error(error);
        enqueueSnackbar("Si è verificato un errore!", {
          variant: "error",
        });
      },
    }
  );

  const { error, mutate } = useMutation(
    (data: Inputs) => {
      return axios.post(API_URL + "tipologie-uso/create", data);
    },
    {
      onSuccess: (data) => {
        const { id } = data.data;
        if (id) {
          queryClient.invalidateQueries();
          enqueueSnackbar("Tipologia Uso Creata", { variant: "success" });
          closeDialog();
        }
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(error?.response?.data?.error ?? "Si è verificato un errore!", {
          variant: "error",
        });
      },
    }
  );

  const { mutate: mutateUpdate } = useMutation(
    (data: Inputs) => {
      return axios.post(API_URL + "tipologie-uso/update", { ...data });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries();
        enqueueSnackbar("Tipologia Uso Aggiornata", { variant: "success" });
        closeDialog();
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(error?.response?.data?.error ?? "Si è verificato un errore!", {
          variant: "error",
        });
      },
    }
  );

  const {
    control,
    reset,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<Inputs>({
    mode: "all",
    resolver: yupResolver(schema),
  });
  const onSubmit = (data: Inputs) => {
    if (id !== undefined) mutateUpdate({ ...data, id });
    else mutate({ ...data });
  };

  useEffect(() => {
    setLoaded(false);
    if (id === undefined) {
      reset(defaultValues);
      setLoaded(true);
    } else refetch();
  }, [reset, setValue, id, refetch]);

  const [animations, setAnimations] = useState(false);
  useEffect(() => {
    let timeout = 0;
    if (loaded) {
      timeout = window.setTimeout(() => setAnimations(true), 1);
    }
    return () => {
      if (timeout !== 0) clearTimeout(timeout);
    };
  }, [loaded]);

  let errorMsg = error?.response?.data?.error ?? "";

  let title = id === undefined ? "Crea Tipologia Uso" : "Modifica Tipologia Uso";

  const { data: tipiSpec } = useQuery<
    { tipologie: RisorseTipologie[]; specifiche: RisorseSpecifiche[] },
    AxiosError
  >(["selectTipSpec"], () => axios.get(API_URL + "risorse/selectTipSpec").then((res) => res.data), {
    refetchOnWindowFocus: false,
    staleTime: 1e5,
    cacheTime: 1e5,
    onError: (error: AxiosError) => {
      enqueueSnackbar(error?.response?.data?.message ?? "Si è verificato un errore!", {
        variant: "error",
      });
    },
  });

  if (!loaded)
    return (
      <Box>
        <DialogTitle>{title}</DialogTitle>
        <Loading />
      </Box>
    );

  return (
    <Box className={animations ? "" : "noAnimation"}>
      <DialogTitle>{title}</DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Grid container columnSpacing={1}>
            <Grid item xs={12}>
              <Controller
                name="descrizioneUso"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    required
                    variant="standard"
                    label="Descrizione"
                    error={Boolean(errors.descrizioneUso)}
                    helperText={errors.descrizioneUso?.message ?? " "}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="Risorse_TipologieId"
                control={control}
                defaultValue={1}
                render={({ field }) => (
                  <FormControl {...field} fullWidth variant="standard" sx={{ mb: 3 }}>
                    <InputLabel>Tipologia</InputLabel>
                    <Select
                      label="Tipologia"
                      value={field.value === null ? "" : field.value}
                      onChange={(e) =>
                        setValue("Risorse_TipologieId", parseInt("" + e.target.value))
                      }
                    >
                      {tipiSpec?.tipologie?.map((tip) => (
                        <MenuItem key={tip.id} value={tip.id}>
                          {tip.definizione}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="minPartecipanti"
                control={control}
                defaultValue={0}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="standard"
                    sx={{ width: 150, mr: 2 }}
                    label="Min Partecipanti"
                    type="number"
                    error={Boolean(errors.minPartecipanti)}
                    helperText={errors.minPartecipanti?.message ?? " "}
                  />
                )}
              />
              <Controller
                name="maxPartecipanti"
                control={control}
                defaultValue={0}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="standard"
                    sx={{ width: 150, mr: 2 }}
                    label="Max Partecipanti"
                    type="number"
                    error={Boolean(errors.maxPartecipanti)}
                    helperText={errors.maxPartecipanti?.message ?? " "}
                  />
                )}
              />
              <Controller
                name="sigla"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    required
                    variant="standard"
                    label="Sigla"
                    error={Boolean(errors.sigla)}
                    helperText={errors.sigla?.message ?? " "}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="note"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="standard"
                    label="Note"
                    error={Boolean(errors.note)}
                    helperText={errors.note?.message ?? " "}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button sx={{ mt: 2 }} onClick={closeDialog}>
            Chiudi
          </Button>

          {!id && (
            <Button type="submit" sx={{ mt: 2 }}>
              Crea Tipologia Uso
            </Button>
          )}
          {id && (
            <Button type="submit" sx={{ mt: 2 }}>
              Modifica Tipologia Uso
            </Button>
          )}
        </DialogActions>
      </form>
      {errorMsg && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errorMsg}
        </Alert>
      )}
    </Box>
  );
}
